import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FiCheck } from "react-icons/fi";
import { BsClock } from "react-icons/bs"; // Icon for the trial banner
import { axiosApi } from "config";
import { toast } from "react-toastify";
import moment from "moment";
import { updateUser } from "store/slices/auth";

// Price ID mapping for each plan and billing period
const priceIdMapping = {
  starter: {
    monthly: "price_1Qi2gwKNhiGCKJmEafx0xpOv",
    yearly: "price_1Qi2gwKNhiGCKJmEpBflgWpd",
  },
  essential: {
    monthly: "price_1Qi2iSKNhiGCKJmEv7MAStCz",
    yearly: "price_1Qi2ioKNhiGCKJmEFtnleEv3",
  },
  team: {
    monthly: "price_1Qi2tLKNhiGCKJmEFiCCH8jm",
    yearly: "price_1Qi2voKNhiGCKJmExR3BqcYn",
  },
  agency: {
    monthly: "price_1Qi2wUKNhiGCKJmEdYNMekTa",
    yearly: "price_1Qi2x5KNhiGCKJmE3nUI5YVw",
  },
};

const SubscriptionPlans = () => {
  const dispatch = useDispatch();
  const [selectedPlanToggle, setSelectedPlanToggle] = useState("monthly");
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get user/token from Redux
  const { token, user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  // Fetch plan data from backend on mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axiosApi.get("/api/plans");
        setPlans(response.data.plans);
      } catch (error) {
        console.error("Error fetching plans:", error);
        toast.error("Failed to load plans.");
      }
    };
    fetchPlans();
  }, []);

  // Subscribe / Start trial
  const handleSubscribe = async (plan) => {
    try {
      setLoading(true);

      // If user not logged in -> store plan in localStorage & redirect
      if (!token) {
        localStorage.setItem(
          "selectedPlan",
          JSON.stringify({
            name: plan.name.toLowerCase(),
            billingCycle: selectedPlanToggle,
            priceId:
              priceIdMapping[plan.name.toLowerCase()][selectedPlanToggle],
            trialStartDate: new Date().toISOString(),
          })
        );
        toast.success(
          "Sign in to start your 14-day free trial. No credit card required!"
        );
        navigate("/auth/sign-in");
        return;
      }

      // If user is logged in
      const planKey = plan.name.toLowerCase();
      const priceId = priceIdMapping[planKey]?.[selectedPlanToggle];
      if (!priceId) {
        toast.error("Price ID not found for the selected plan.");
        return;
      }

      // Check trial status from the backend
      const userInfo = await axiosApi.get("/user/info");
      const { isInTrial, trialStartDate: trialStartDateVal } = userInfo.data;

      const trialStartDate = new Date(trialStartDateVal);
      const currentDate = new Date();
      const daysSinceTrialStart = Math.floor(
        (currentDate - trialStartDate) / (1000 * 60 * 60 * 24)
      );

      // If trial ended, redirect to Stripe checkout
      if (isInTrial && daysSinceTrialStart >= 14) {
        const { data } = await axiosApi.post(
          "/api/subscription/create-checkout-session",
          {
            plan: planKey,
            priceId,
            billingCycle: selectedPlanToggle,
            userId: user.id,
            customerEmail: user.email,
          },
          { withCredentials: true }
        );
        window.location.href = data.url; // Stripe Checkout
      }
      // If still in trial, just show remaining days
      else if (isInTrial && daysSinceTrialStart < 14) {
        toast.info(
          `You are currently in your trial period. ${
            14 - daysSinceTrialStart
          } days remaining.`
        );
        navigate("/main/default");
      }
      // If no trial -> start a new trial
      else if (!isInTrial) {
        const { data } = await axiosApi.post(
          "/api/subscription/subscribe",
          { planId: planKey },
          { withCredentials: true }
        );
        toast.success(
          `${data.message}. Your trial will end on ${moment(
            data.trialEndDate
          ).format("MMMM Do, YYYY / h a")}`
        );
        const { data: userInfo } = await axiosApi.get("/user/info");
        dispatch(updateUser(userInfo.user));
      }
    } catch (error) {
      console.error("Error handling subscription:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to process subscription request."
      );
    } finally {
      setLoading(false);
    }
  };

  // Calculate yearly savings
  const calculateYearlySavings = (monthlyPrice, yearlyPrice) => {
    const monthlyTotal = monthlyPrice * 12;
    const savings = (
      ((monthlyTotal - yearlyPrice) / monthlyTotal) *
      100
    ).toFixed(0);
    return savings;
  };

  // If user is in trial, parse the trial end date for display
  const trialEndDateFormatted = user?.trial?.trialEndDate
    ? moment(user.trial.trialEndDate).format("MMMM D, YYYY")
    : null;

  return (
    <section id="pricing" className="bg-white py-24">
      <div className="container mx-auto px-6">
        {/* Show banner if user is in trial */}
        {user?.trial?.isInTrial && trialEndDateFormatted && (
          <div className="mb-6 flex items-center justify-center rounded-lg bg-purple-100 px-6 py-4 text-purple-900">
            <BsClock className="mr-3 text-xl" />
            <p className="text-md font-medium">
              🎉 Your free trial is active! It ends on{" "}
              <strong>{trialEndDateFormatted}</strong>.
            </p>
          </div>
        )}

        {/* Title */}
        <div className="mb-8 text-center">
          <h2 className="mb-4 text-4xl font-bold text-gray-900">
            Simple, Transparent Pricing
          </h2>
          {!!user && !user?.trial?.isInTrial && (
            <p className="text-lg text-gray-600">
              Start with a 14-day free trial. No credit card required.
            </p>
          )}
          <div className="mt-8 flex items-center justify-center gap-4">
            <button
              onClick={() => setSelectedPlanToggle("monthly")}
              className={`rounded-full px-6 py-2 ${
                selectedPlanToggle === "monthly"
                  ? "bg-purple-100 text-purple-800"
                  : "text-gray-600 hover:bg-gray-100"
              } transition duration-300`}
            >
              Monthly
            </button>
            <button
              onClick={() => setSelectedPlanToggle("yearly")}
              className={`rounded-full px-6 py-2 ${
                selectedPlanToggle === "yearly"
                  ? "bg-purple-100 text-purple-800"
                  : "text-gray-600 hover:bg-gray-100"
              } transition duration-300`}
            >
              Yearly
              <span className="ml-2 text-sm text-purple-600">
                Save up to{" "}
                {calculateYearlySavings(
                  plans[0]?.monthlyPrice || 0,
                  plans[0]?.yearlyPrice || 0
                )}
                %
              </span>
            </button>
          </div>
        </div>

        {/* Plan Cards */}
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          {plans.length === 0 ? (
            <p>Loading plans...</p>
          ) : (
            plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative rounded-2xl border bg-white p-8 shadow-xl ${
                  plan.popular ? "border-purple-500" : "border-gray-200"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 transform">
                    <span className="rounded-full bg-gradient-to-r from-[#7F03E2] to-[#A13BF2] px-4 py-1 text-sm text-white">
                      Most Popular
                    </span>
                  </div>
                )}
                <h3 className="mb-4 text-xl font-bold text-gray-900">
                  {plan.name}
                </h3>
                <div className="mb-2">
                  <span className="text-4xl font-bold">
                    {selectedPlanToggle === "monthly"
                      ? `$${plan.monthlyPrice}`
                      : `$${plan.yearlyPrice}`}
                  </span>
                  <span className="text-gray-600">/month</span>
                  {selectedPlanToggle === "yearly" && (
                    <div className="mt-1 text-sm text-green-600">
                      Save{" "}
                      {calculateYearlySavings(
                        plan.monthlyPrice,
                        plan.yearlyPrice
                      )}
                      % yearly
                    </div>
                  )}
                </div>
                <p className="mb-4 text-sm text-purple-600">
                  14-day free trial
                </p>
                <ul className="mb-8 space-y-4">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <FiCheck className="flex-shrink-0 text-green-500" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                {!!user && !user?.trial?.isInTrial && (
                  <button
                    className={`w-full rounded-full py-3 font-semibold transition duration-300 ${
                      plan.popular
                        ? "bg-gradient-to-r from-[#7F03E2] to-[#A13BF2] text-white hover:shadow-lg"
                        : "bg-purple-50 text-purple-700 hover:bg-purple-100"
                    }`}
                    onClick={() => handleSubscribe(plan)}
                    disabled={loading || user?.trial?.isInTrial}
                  >
                    {loading ? "Processing..." : "Start Free Trial"}
                  </button>
                )}
              </motion.div>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default SubscriptionPlans;
